import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { SelectCore } from '#/modules/commonComponents/form/select';
import { toast } from '#/utils/toast';

function buildGroupHydration(groupOptions, bomMaterialSelections) {
  const selections = Array.isArray(bomMaterialSelections)
    ? bomMaterialSelections
    : [];
  const initial = {};

  groupOptions.forEach((group) => {
    const existing = selections.find(
      (item) => item.sourceGroupMaterialId === group.sourceGroupMaterialId,
    );
    // 無已選則空字串（「請選擇」）；勿 fallback options[0]
    initial[group.sourceGroupMaterialId] = existing?.selectedMaterialId || '';
  });

  return initial;
}

function buildLegacyHydration(sourceRow, legacyOptions) {
  const current = sourceRow?.selectedMtrId
    || sourceRow?.selectedMtrNo
    || '';
  if (!current) {
    return '';
  }
  const matched = legacyOptions.find((option) => (
    option.value === current
    || option.mtrId === current
    || option.mtrNo === current
  ));
  return matched?.value ?? '';
}

const EMPTY_SELECT_OPTION = { value: '', label: '請選擇' };

function selectionHydrateKey(sourceRow) {
  if (!sourceRow) {
    return 'none';
  }
  const groupPart = (sourceRow.bomMaterialSelections ?? [])
    .map((item) => `${item.sourceGroupMaterialId}:${item.selectedMaterialId || ''}`)
    .join('|');
  const legacyPart = sourceRow.selectedMtrId || sourceRow.selectedMtrNo || '';
  return `${sourceRow.id}:${groupPart}:${legacyPart}`;
}

/**
 * 選料 Dialog：
 * - legacy candidates 模式：一列一種實際料（candidates）
 * - draft-plan：每組替代料選一種實際料（groupMtrOptions）
 * 開啟／換列時自 bomMaterialSelections／selected* hydrate（無已選＝空，勿 fallback 第一筆）。
 */
const ManufacturingMaterialSelectDialog = ({
  open,
  onOpenChange,
  sourceRow = null,
  onConfirm,
}) => {
  const groupOptions = useMemo(
    () => sourceRow?.groupMtrOptions ?? [],
    [sourceRow?.groupMtrOptions],
  );
  const isGroupMode = groupOptions.length > 0;

  const legacyOptions = useMemo(
    () => (sourceRow?.candidates ?? []).map((option) => ({
      value: option.mtrId || option.mtrNo,
      label: [option.mtrNo, option.mtrName].filter(Boolean).join(' '),
      ...option,
    })),
    [sourceRow?.candidates],
  );

  const hydrateKey = open && sourceRow
    ? selectionHydrateKey(sourceRow)
    : 'closed';

  const [hydratedKey, setHydratedKey] = useState(hydrateKey);
  const [selectedValue, setSelectedValue] = useState('');
  const [groupValues, setGroupValues] = useState({});

  if (hydrateKey !== hydratedKey) {
    setHydratedKey(hydrateKey);
    if (open && sourceRow) {
      if (isGroupMode) {
        setGroupValues(buildGroupHydration(
          groupOptions,
          sourceRow.bomMaterialSelections,
        ));
        setSelectedValue('');
      } else {
        setSelectedValue(buildLegacyHydration(sourceRow, legacyOptions));
        setGroupValues({});
      }
    } else {
      setSelectedValue('');
      setGroupValues({});
    }
  }

  function handleOpenChange(nextOpen) {
    onOpenChange(nextOpen);
  }

  function handleConfirm() {
    if (!sourceRow) {
      return;
    }
    if (isGroupMode) {
      const selections = groupOptions.map((group) => {
        const selectedId = groupValues[group.sourceGroupMaterialId];
        const option = group.options.find((item) => item.mtrId === selectedId);
        return {
          sourceGroupMaterialId: group.sourceGroupMaterialId,
          selectedMaterialId: option?.mtrId ?? '',
          selectedMtrNo: option?.mtrNo ?? '',
          selectedMtrName: option?.mtrName ?? '',
        };
      });
      if (selections.some((item) => !item.selectedMaterialId)) {
        toast.warning('請完成當前視窗所有替代料選取');
        return;
      }
      onConfirm?.({ rowId: sourceRow.id, mode: 'group', selections });
      handleOpenChange(false);
      return;
    }

    const selected = legacyOptions.find((option) => option.value === selectedValue);
    if (!selected) {
      toast.warning('請選擇實際料');
      return;
    }

    onConfirm?.({
      rowId: sourceRow.id,
      mode: 'legacy',
      material: {
        mtrId: selected.mtrId,
        mtrNo: selected.mtrNo,
        mtrName: selected.mtrName,
      },
    });
    handleOpenChange(false);
  }

  const confirmDisabled = !sourceRow || (
    isGroupMode
      ? groupOptions.length === 0
      : legacyOptions.length === 0
  );

  return (
    <AppDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={isGroupMode ? '選擇替代料' : '選擇實際料'}
      description={
        isGroupMode
          ? '此候選 BOM 含替代料群組：每組請選一種實際料。'
          : (
            '一列只選一種實際料。若製作量小於總需求，剩餘量請另開一次流程再選另一料（本頁不拆多列）。'
          )
      }
      headerVariant="primary"
      maxWidthClassName={dialogWideMaxWidthClassName}
      contentClassName={cn(dialogContentCompactGapClassName, 'pb-5')}
      closeOnOutsideClick={false}
      onCancel={() => handleOpenChange(false)}
      onConfirm={handleConfirm}
      confirmText="確認選料"
      cancelText="取消"
      confirmDisabled={confirmDisabled}
    >
      {sourceRow ? (
        <div className="space-y-4">
          <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
            <p className="text-xs text-gray-500">工單成品</p>
            <p className="font-medium text-gray-900">
              {sourceRow.mtrNo}
              {' '}
              {sourceRow.mtrName}
            </p>
          </div>

          {isGroupMode ? (
            <div className={cn('@container/alt-pick')}>
              <div
                className={cn(
                  'grid grid-cols-1 gap-5',
                  '@xl/alt-pick:grid-cols-2',
                )}
              >
                {groupOptions.map((group) => {
                  const options = [
                    EMPTY_SELECT_OPTION,
                    ...group.options.map((option) => ({
                      value: option.mtrId,
                      label: [option.mtrNo, option.mtrName].filter(Boolean).join(' '),
                    })),
                  ];
                  return (
                    <SelectCore
                      key={group.sourceGroupMaterialId}
                      id={`group-select-${group.sourceGroupMaterialId}`}
                      label={
                        [group.sourceGroupMtrNo, group.sourceGroupMtrName]
                          .filter(Boolean)
                          .join(' ')
                        || '替代料'
                      }
                      options={options}
                      value={groupValues[group.sourceGroupMaterialId] ?? ''}
                      onChange={(value) => {
                        setGroupValues((prev) => ({
                          ...prev,
                          [group.sourceGroupMaterialId]: value ?? '',
                        }));
                      }}
                      hideErrorMessage
                      containerClassName="mb-0!"
                      noOptionsMessage="無可選料"
                    />
                  );
                })}
              </div>
            </div>
          ) : (
            <SelectCore
              id="manufacturingMaterialSelect"
              label="實際料"
              options={[EMPTY_SELECT_OPTION, ...legacyOptions]}
              value={selectedValue}
              onChange={(value) => setSelectedValue(value ?? '')}
              hideErrorMessage
              containerClassName="mb-0!"
              noOptionsMessage="無可選料"
            />
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">尚無需求列</p>
      )}
    </AppDialog>
  );
};

ManufacturingMaterialSelectDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  sourceRow: PropTypes.shape({
    id: PropTypes.string,
    mtrId: PropTypes.string,
    mtrNo: PropTypes.string,
    mtrName: PropTypes.string,
    selectedMtrId: PropTypes.string,
    selectedMtrNo: PropTypes.string,
    selectedMtrName: PropTypes.string,
    candidates: PropTypes.arrayOf(PropTypes.object),
    groupMtrOptions: PropTypes.arrayOf(PropTypes.object),
    bomMaterialSelections: PropTypes.arrayOf(PropTypes.object),
  }),
  onConfirm: PropTypes.func,
};

export default ManufacturingMaterialSelectDialog;
